import { useContext, useEffect, useState } from 'react';
import { Button, CircularProgress } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { Context } from '../../../AuthContext';
import { useCategory } from '../../../CategoryContext';
import SelectVerticalCalendarDialog from './SelectVerticalCalendarDialog';
import AddEventDialog from './AddEventDialog';

const GOOGLE_CALENDAR_SCOPE = 'https://www.googleapis.com/auth/calendar.calendarlist.readonly';

const INTRO_TEXT =
  'For the best experience, make sure the calendar currently synced below is a newly created calendar in Google Calendar for this Vertical. ' +
  'This keeps your personal calendar private while allowing you to easily share events with collaborators.';

// Encode a Google calendar id for use in the settings URL: base64url, no padding.
const calendarSettingsUrl = (calendarId) => {
  try {
    const b64 = btoa(unescape(encodeURIComponent(calendarId)));
    const urlSafe = b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    return `https://calendar.google.com/calendar/u/0/r/settings/calendar/${urlSafe}`;
  } catch (_) {
    return 'https://calendar.google.com/calendar/u/0/r';
  }
};

const VerticalCalendar = ({ verticalId }) => {
  const { user } = useContext(Context);
  const { verticals } = useCategory();
  const [phase, setPhase] = useState('loading'); // loading | connect | ready | error
  const [calendars, setCalendars] = useState([]); // owner's calendar list (only fetched if connected)
  const [verticalCalendar, setVerticalCalendar] = useState(null); // {calendar_id, summary, backgroundColor}
  const [selectOpen, setSelectOpen] = useState(false);
  const [eventOpen, setEventOpen] = useState(false);
  const [iframeNonce, setIframeNonce] = useState(0);

  // Roles for THIS vertical.
  const myVertical = (verticals || []).find((v) => v.id === verticalId);
  const isOwner = myVertical?.status === 'Created';
  const isAdvancedEditor =
    isOwner || (myVertical?.status === 'Joined' && myVertical?.permission === 'AdvancedEditor');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!user || !verticalId) return;
      try {
        const token = await user.getIdToken();
        const headers = { Authorization: `Bearer ${token}` };

        // Fetch the vertical's saved calendar first — this works for any user
        // with vertical access, including non-owners who haven't connected
        // their own Google account.
        const vcRes = await fetch(
          `${
            process.env.REACT_APP_FLASK_WEBSERVER
          }oauth/google/vertical-calendar?vertical_id=${encodeURIComponent(verticalId)}`,
          { headers }
        );
        if (cancelled) return;
        if (vcRes.ok) {
          const vc = await vcRes.json();
          setVerticalCalendar(vc.calendar_id ? vc : null);
        }

        // If owner, also fetch their Google calendar list for the Select dialog.
        // Non-owners don't need this.
        if (isOwner) {
          const statusRes = await fetch(
            `${process.env.REACT_APP_FLASK_WEBSERVER}oauth/google/status`,
            { headers }
          );
          if (!statusRes.ok) throw new Error(`status_${statusRes.status}`);
          const status = await statusRes.json();
          if (cancelled) return;
          const hasCalendar =
            status.connected && (status.scopes || []).includes(GOOGLE_CALENDAR_SCOPE);
          if (!hasCalendar) {
            setPhase('connect');
            return;
          }
          const calRes = await fetch(
            `${process.env.REACT_APP_FLASK_WEBSERVER}oauth/google/calendars`,
            { headers }
          );
          if (calRes.status === 401) {
            if (!cancelled) setPhase('connect');
            return;
          }
          if (!calRes.ok) throw new Error(`calendars_${calRes.status}`);
          const calData = await calRes.json();
          if (cancelled) return;
          setCalendars(calData.calendars || []);
        }

        if (!cancelled) setPhase('ready');
      } catch (_) {
        if (!cancelled) setPhase('error');
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [user, verticalId, isOwner]);

  // Reload the embed when the user returns to this tab (e.g. after adding
  // an event on Google Calendar in a sibling tab).
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        setIframeNonce((n) => n + 1);
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, []);

  const handleSelect = async (cal) => {
    if (!user || !verticalId) return;
    try {
      const token = await user.getIdToken();
      const res = await fetch(
        `${process.env.REACT_APP_FLASK_WEBSERVER}oauth/google/vertical-calendar`,
        {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            vertical_id: verticalId,
            calendar_id: cal.id,
            summary: cal.summary,
            backgroundColor: cal.backgroundColor,
          }),
        }
      );
      if (res.ok) {
        setVerticalCalendar({
          calendar_id: cal.id,
          summary: cal.summary,
          backgroundColor: cal.backgroundColor,
        });
        setIframeNonce((n) => n + 1);
      }
    } catch (_) {
      /* swallow */
    } finally {
      setSelectOpen(false);
    }
  };

  const buildEmbedUrl = () => {
    if (!verticalCalendar?.calendar_id) return '';
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    const params = new URLSearchParams();
    params.append('ctz', tz);
    params.append('mode', 'MONTH');
    params.append('wkst', '1');
    params.append('showTitle', '0');
    params.append('showPrint', '0');
    params.append('src', verticalCalendar.calendar_id);
    if (verticalCalendar.backgroundColor) {
      params.append('color', verticalCalendar.backgroundColor);
    }
    return `https://calendar.google.com/calendar/embed?${params.toString()}`;
  };

  // -----------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------
  const containerStyle = {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    paddingRight: 8,
    paddingBottom: 8,
  };

  if (phase === 'loading') {
    return (
      <div style={{ ...containerStyle, alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress size={28} />
      </div>
    );
  }

  if (phase === 'connect') {
    // Only owners hit this — they haven't connected Google yet.
    return (
      <div
        style={{
          ...containerStyle,
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
        }}
      >
        <img
          src="/workbench/google.svg"
          alt="Google"
          style={{ width: 48, height: 48, marginBottom: 16 }}
        />
        <h3 style={{ margin: 0, marginBottom: 8 }}>Connect your Google account</h3>
        <p style={{ margin: 0, marginBottom: 16, color: '#475467', maxWidth: 480 }}>
          Link your Google account to set up a calendar for this vertical.
        </p>
        <RouterLink
          to="/profile/connected-accounts"
          style={{
            background: '#356AC3',
            color: '#fff',
            padding: '10px 20px',
            borderRadius: 6,
            textDecoration: 'none',
            fontWeight: 600,
          }}
        >
          Go to Connected Accounts
        </RouterLink>
      </div>
    );
  }

  if (phase === 'error') {
    return (
      <div
        style={{
          ...containerStyle,
          alignItems: 'center',
          justifyContent: 'center',
          color: '#B42318',
          textAlign: 'center',
        }}
      >
        Could not load the vertical calendar. Please try again later.
      </div>
    );
  }

  const hasCalendar = !!verticalCalendar?.calendar_id;
  const eventDisabled = !hasCalendar;

  return (
    <div style={containerStyle}>
      {isOwner && (
        <i
          style={{
            margin: '0 0 12px 0',
            color: '#475467',
            fontSize: 13,
            lineHeight: 1.4,
            textAlign: 'left',
          }}
        >
          {INTRO_TEXT}
        </i>
      )}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 12,
        }}
      >
        {isOwner && (
          <Button
            variant="contained"
            onClick={() => setSelectOpen(true)}
            sx={{ textTransform: 'none' }}
          >
            Select Calendar
          </Button>
        )}
        {isAdvancedEditor && (
          <Button
            variant="contained"
            disabled={eventDisabled}
            onClick={() => setEventOpen(true)}
            title={eventDisabled ? 'Select a calendar first.' : ''}
            sx={{ textTransform: 'none' }}
          >
            Add Event
          </Button>
        )}
        {isOwner && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Button
              variant="contained"
              disabled={!hasCalendar}
              onClick={() => {
                window.open(
                  calendarSettingsUrl(verticalCalendar.calendar_id),
                  '_blank',
                  'noopener,noreferrer'
                );
              }}
              title={hasCalendar ? '' : 'Select a calendar first.'}
              sx={{ textTransform: 'none' }}
            >
              Manage Access
            </Button>
          </div>
        )}
      </div>

      {!hasCalendar ? (
        <div
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#667085',
            textAlign: 'center',
            padding: 24,
          }}
        >
          {isOwner
            ? 'Click "Select Calendar" above to choose the Google calendar for this vertical.'
            : 'No calendar has been set up for this vertical yet. The owner needs to select one.'}
        </div>
      ) : (
        <iframe
          key={`${iframeNonce}:${verticalCalendar.calendar_id}`}
          title="Vertical Calendar"
          src={buildEmbedUrl()}
          style={{ border: 0, width: '100%', flex: 1, minHeight: 0, border: '1px solid black' }}
        />
      )}

      {isOwner && (
        <SelectVerticalCalendarDialog
          open={selectOpen}
          calendars={calendars}
          initialSelectedId={verticalCalendar?.calendar_id || ''}
          onClose={() => setSelectOpen(false)}
          onSelect={handleSelect}
        />
      )}
      {isAdvancedEditor && (
        <AddEventDialog
          open={eventOpen}
          lockCalendar
          calendars={
            hasCalendar
              ? [
                  {
                    id: verticalCalendar.calendar_id,
                    summary: verticalCalendar.summary || 'Vertical calendar',
                    backgroundColor: verticalCalendar.backgroundColor,
                    primary: false,
                  },
                ]
              : []
          }
          onClose={() => setEventOpen(false)}
        />
      )}
    </div>
  );
};

export default VerticalCalendar;
